import React, {
  memo, useRef, useCallback, useMemo, useState, forwardRef, useImperativeHandle,
} from 'react';
import ReactDOM from 'react-dom';
import { LexicalEditor, REDO_COMMAND, UNDO_COMMAND } from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { HistoryPlugin, createEmptyHistoryState } from '@lexical/react/LexicalHistoryPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { IInlineBox, InlineBoxType } from '$/components/InlineBox/types';
import type { MarkResult } from '$/components/InlineBox/types';
import classNames from 'classnames';
import styles from './index.module.scss';
import { InlineBoxNode, MentionNode } from './nodes';
import {
  DecoratorBackspacePlugin,
  DecoratorHorizontalArrowPlugin,
  DecoratorVerticalArrowPlugin,
  EditablePlugin,
  EnterSubmitPlugin,
  InputBridgePlugin,
  MarkOrderTransformPlugin,
  MaxLengthPlugin,
  MentionTriggerPlugin,
  PastePlainTextPlugin,
  PostTaskSelectionGuardPlugin,
  SelectionSyncPlugin,
  SlashTriggerPlugin,
  isDirectSlashTriggerKey,
} from './plugins';
import {
  AtImageOption,
  AtImagePopupRenderer,
  EditorSerializedContent,
  IInputBridge,
  InlineBoxClickHandler,
  MarkInsertOptions,
  MentionOption,
  MentionPopupRenderer,
  RenderMarkPopover,
  SkillOption,
  SkillPopupRenderer,
} from './types';
import { buildEditorFromContent } from './editor';
import {
  useEditorChangeHandler,
  useEditorUiState,
  useInlineSuggestionController,
  useLatestRef,
  useMentionMenuController,
} from './hooks';
import { InputTextAreaContext } from './context';
import type { MentionMenuState } from './plugins';
import SuggestionPopup from './SuggestionPopup';
import {
  shouldDeferInputHistoryShortcutToCanvas,
  shouldSkipInputHistoryShortcutForExternalEditable,
} from '../inputHistoryShortcut';

let latestHistoryShortcutOwner: symbol | null = null;

/** Portal 容器：将弹框渲染到 body，避免被外部 overflow 截断 */
function PopupPortal({
  containerRef,
  children,
}: {
  containerRef: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}) {
  if (!children || typeof document === 'undefined') return null;
  const container = containerRef.current;
  if (!container) return null;
  const rect = container.getBoundingClientRect();
  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        overflow: 'visible',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

export interface MediaInlineCounts {
  image: number;
  video: number;
  document: number;
}

export interface MediaLimitConfig {
  /**
   * 插入媒体前的校验函数。
   * 返回 null 表示允许插入，返回 InsertRejectedReason 表示拒绝。
   */
  validate: (
    inlineBox: IInlineBox,
    counts: MediaInlineCounts,
  ) => InsertRejectedReason | null;
}

export interface InsertRejectedReason {
  type: 'image' | 'video' | 'document';
  messageKey: string;
  messageParams?: Record<string, string | number>;
}

export interface IInputTextAreaProps {
  className?: string;
  textareaClassName?: string;
  placeholderClassName?: string;
  placeholder?: string;
  /** 编辑器已有行内标签但无用户文本时，紧跟标签展示且不参与序列化的引导文案。 */
  inlinePlaceholder?: string;
  /** 初始内容，仅挂载时使用；后续内容更新请通过 bridge.setValue() */
  initialValue?: EditorSerializedContent;
  onChange?: (params: { value: EditorSerializedContent; }) => void;
  onEnter?: (value: EditorSerializedContent) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  /** 编辑器内容区聚焦时回调 */
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  /** 编辑器内容区失焦时回调 */
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  onSelect?: (params: { id?: string; }) => void;
  disabled?: boolean;
  maxLength?: number;
  mentionOptions?: MentionOption[];
  onMentionSelect?: (option: MentionOption) => void;
  renderMentionPopup?: MentionPopupRenderer;
  atImageOptions?: AtImageOption[];
  onAtImageSelect?: (option: AtImageOption) => void;
  renderAtImagePopup?: AtImagePopupRenderer;
  /** @图片联想弹框相对触发目标的位置，默认向下展开 */
  atImagePopupPlacement?: 'top' | 'bottom';
  /** @图片联想弹框数据为空时展示的默认文案 */
  atImagePopupEmptyText?: string;
  /** @图片联想弹框数据为空时自定义渲染 */
  renderAtImagePopupEmpty?: () => React.ReactNode;
  /** @图片联想弹框列表首项自定义渲染 */
  renderAtImagePopupBeforeItem?: () => React.ReactNode;
  skillOptions?: SkillOption[];
  onSkillSelect?: (option: SkillOption) => void;
  renderSkillPopup?: SkillPopupRenderer;
  /** /技能联想弹框数据为空时自定义渲染 */
  renderSkillPopupEmpty?: () => React.ReactNode;
  /** /技能联想弹框列表首项自定义渲染 */
  renderSkillPopupBeforeItem?: () => React.ReactNode;
  /** bridge 就绪时回调，适合在此处做初始内容回灌等一次性操作 */
  onBridgeReady?: (bridge: IInputBridge) => void;
  afterInsertInlineNode?: (params: { inlineBox: IInlineBox; nodeKey: string; }) => Promise<void>;
  /** 媒体数量限制，传入后 bridge.insertInlineNode 内部校验 */
  mediaLimit?: MediaLimitConfig;
  /** 插入被限制拒绝时回调（超限、互斥等） */
  onInsertRejected?: (reason: InsertRejectedReason) => void;
  /** Mention 数量上限 */
  maxMentions?: number;
  /** 超出 Mention 上限时回调 */
  onMaxMentionsReached?: () => void;
  /**
   * Mark 点击弹框自定义渲染。
   * - 返回 ReactNode → 替换默认 MarkResultPopover
   * - 返回 undefined → 使用默认 MarkResultPopover
   * - 不传此 prop → 所有 Mark 使用默认弹框
   */
  renderMarkPopover?: RenderMarkPopover;
  /** 点击输入框内 inlineBox 时回调，参数与 MediaInput 的 onItemClick 保持一致 */
  onInlineBoxClick?: InlineBoxClickHandler;
  /**
   * 数据驱动模式：Mark 插入后自动调用此函数获取识别结果列表。
   * 组件内部自动管理 loading 和默认 MarkResultPopover 的渲染。
   * 优先级：renderMarkPopover > fetchMarkResults > 默认空弹框
   */
  fetchMarkResults?: (params: { inlineBox: IInlineBox; nodeKey: string; }) => Promise<MarkResult[]>;
  /** 是否开启 @图片联想词，默认 true */
  enableAtImageSuggestion?: boolean;
  /** 是否开启 /skill 联想词，默认 true */
  enableSkillSuggestion?: boolean;
  /** 是否在挂载后自动聚焦编辑器，默认 false */
  autoFocus?: boolean;
}

/** 文本输入组件负责把外部结构化内容与 Lexical 编辑器实例做双向同步。 */
// eslint-disable-next-line prefer-arrow-callback
const InputTextArea = forwardRef<IInputBridge, IInputTextAreaProps>(function InputTextArea(props, ref) {
  const {
    placeholder = '请输入内容...',
    initialValue,
    onChange,
    onEnter,
    onKeyDown: onKeyDownProp,
    onFocus,
    onBlur,
    disabled = false,
    maxLength,
    mentionOptions = [],
    onSelect,
    onMentionSelect,
    onBridgeReady,
    afterInsertInlineNode,
    renderMentionPopup,
    atImageOptions = [],
    onAtImageSelect,
    renderAtImagePopup,
    atImagePopupPlacement = 'bottom',
    atImagePopupEmptyText,
    renderAtImagePopupEmpty,
    renderAtImagePopupBeforeItem,
    skillOptions = [],
    onSkillSelect,
    renderSkillPopup,
    renderSkillPopupEmpty,
    renderSkillPopupBeforeItem,
    mediaLimit,
    onInsertRejected,
    className,
    textareaClassName,
    placeholderClassName,
    inlinePlaceholder,
    maxMentions,
    onMaxMentionsReached,
    renderMarkPopover,
    onInlineBoxClick,
    fetchMarkResults,
    enableAtImageSuggestion = true,
    enableSkillSuggestion = false,
    autoFocus = false,
  } = props;

  // ─── ref-based bridge 暴露 ──────────────────────────────────────────────
  const internalBridgeRef = useRef<IInputBridge | null>(null);
  const onBridgeReadyRef = useRef(onBridgeReady);
  onBridgeReadyRef.current = onBridgeReady;

  useImperativeHandle(ref, () => ({
    focus: () => internalBridgeRef.current?.focus(),
    clear: () => internalBridgeRef.current?.clear(),
    insertInlineNode: (inlineBox, options) => internalBridgeRef.current?.insertInlineNode(inlineBox, options) ?? false,
    insertText: (text) => internalBridgeRef.current?.insertText(text),
    setValue: (content, options) => internalBridgeRef.current?.setValue(content, options),
    resetHistoryStack: () => internalBridgeRef.current?.resetHistoryStack(),
    updateMarkData: (nodeKey, patch) => internalBridgeRef.current?.updateMarkData(nodeKey, patch),
    setMarkLoading: (nodeKey, loading) => internalBridgeRef.current?.setMarkLoading(nodeKey, loading),
    getMarkResults: (nodeKey) => markResultsMapRef.current.get(nodeKey),
  }), []);

  const handleRegisterBridge = useCallback((bridge: IInputBridge | null) => {
    internalBridgeRef.current = bridge;
    if (bridge) {
      onBridgeReadyRef.current?.(bridge);
    }
  }, []);

  const inputContainerRef = useRef<HTMLDivElement>(null);
  const editorRootRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<LexicalEditor | null>(null);
  const historyShortcutOwnerRef = useRef(Symbol('InputTextareaHistoryOwner'));
  const {
    isFocused,
    isComposing,
    isComposingRef,
    handleBlur,
    handleCompositionEnd,
    handleCompositionStart,
    handleFocus,
  } = useEditorUiState();
  const disabledRef = useLatestRef(disabled);
  const [atImageMenuState, setAtImageMenuState] = useState<MentionMenuState | null>(null);
  const [skillMenuState, setSkillMenuState] = useState<MentionMenuState | null>(null);
  const allowNextAtImageTriggerRef = useRef(false);
  const allowNextSkillTriggerRef = useRef(false);
  const atImageMenuStateRef = useRef<MentionMenuState | null>(null);
  const skillMenuStateRef = useRef<MentionMenuState | null>(null);
  atImageMenuStateRef.current = atImageMenuState;
  skillMenuStateRef.current = skillMenuState;

  // ─── 从当前编辑器内容派生图片/视频/文件列表，作为默认 atImageOptions ──────
  const [editorContent, setEditorContent] = useState<EditorSerializedContent>([]);
  const derivedAtImageOptions: AtImageOption[] = useMemo(() => editorContent
    .filter((fragment) => fragment.type === 'inline-box'
      && (fragment.value.type === InlineBoxType.Image
        || fragment.value.type === InlineBoxType.Video
        || fragment.value.type === InlineBoxType.File))
    .map((fragment, idx) => {
      const { value } = fragment as { value: { type: InlineBoxType; data: AtImageOption['image']; }; type: string; };
      const { data } = value;
      return {
        id: data.index || `${value.type}-${idx}`,
        name: data.name,
        keywords: [data.name, data.mediaType || value.type].filter(Boolean),
        image: data,
      };
    }), [editorContent]);

  const resolvedAtImageOptions = atImageOptions.length > 0 ? atImageOptions : derivedAtImageOptions;
  const useAtImageTrigger = enableAtImageSuggestion;

  /** 联想弹框插入素材时优先使用触发词删除后的精确选区，避免插入到缓存光标位置。 */
  const insertInlineBoxBySuggestion = useCallback((inlineBox: IInlineBox, options?: MarkInsertOptions) => {
    internalBridgeRef.current?.insertInlineNode(inlineBox, options);
  }, []);

  const markAsLatestHistoryShortcutOwner = useCallback(() => {
    latestHistoryShortcutOwner = historyShortcutOwnerRef.current;
  }, []);

  const scrollCaretIntoView = useCallback(() => {
    // 双 rAF：等 Lexical 提交 DOM + 行内装饰节点旁插入空格/换行后选区稳定，再量测光标
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const root = editorRootRef.current;
        if (!root) return;
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const { anchorNode, focusNode, focusOffset } = selection;
        if (!anchorNode || !focusNode || !root.contains(anchorNode) || !root.contains(focusNode)) {
          return;
        }

        // 优先用 focus 折叠 Range 量「真实光标」，避免 getRangeAt(0) 在行内元素后偏向文档前部矩形
        let rect: DOMRect | null = null;
        try {
          const caretRange = document.createRange();
          caretRange.setStart(focusNode, focusOffset);
          caretRange.setEnd(focusNode, focusOffset);
          const r = caretRange.getBoundingClientRect();
          if (r.width > 0 || r.height > 0) {
            rect = r;
          } else {
            const rects = caretRange.getClientRects();
            if (rects.length > 0) {
              rect = rects[rects.length - 1];
            }
          }
        } catch {
          rect = null;
        }

        if (!rect || (rect.width === 0 && rect.height === 0)) {
          const range = selection.getRangeAt(0);
          rect = range.getBoundingClientRect();
          const isDegenerate = rect.width === 0 && rect.height === 0;
          if (isDegenerate) {
            const clientRects = range.getClientRects();
            if (clientRects.length > 0) {
              rect = clientRects[clientRects.length - 1];
            } else {
              let node: Node | null = focusNode;
              let found: DOMRect | null = null;
              while (node && node !== root) {
                if (node instanceof HTMLElement && root.contains(node)) {
                  const r = node.getBoundingClientRect();
                  if (r.height > 0 || r.width > 0) {
                    found = r;
                    break;
                  }
                }
                node = node.parentNode;
              }
              if (!found) return;
              rect = found;
            }
          }
        }

        const containerRect = root.getBoundingClientRect();

        // 折叠光标不可能同时高于可视区域顶部且低于底部；若如此则为错误矩形
        if (rect.top < containerRect.top && rect.bottom > containerRect.bottom) {
          return;
        }

        // 整块段落类矩形不应驱动「对齐到光标」的纵向滚动，否则在行内元素后输入易误判并滚到顶
        const lineSlop = 40;
        const looksLikeWideBlock = rect.height > lineSlop * 1.5
          && rect.width > root.clientWidth * 0.45;

        if (rect.top < containerRect.top && !looksLikeWideBlock) {
          root.scrollTop -= containerRect.top - rect.top;
        } else if (rect.bottom > containerRect.bottom && !looksLikeWideBlock) {
          root.scrollTop += rect.bottom - containerRect.bottom;
        }
      });
    });
  }, []);

  const {
    activeMentionIndex,
    closeMentionMenu,
    filteredMentionOptions,
    handleEnterCommand: handleMentionEnterCommand,
    handleKeyDown: handleMentionKeyDown,
    insertMention,
    mentionMenuState,
    setMentionMenuState,
  } = useMentionMenuController({
    mentionOptions: useAtImageTrigger ? [] : mentionOptions,
    editorRef,
    onKeyDown: onKeyDownProp,
    onMentionSelect,
    isComposing,
    maxMentions,
    onMaxMentionsReached,
  });

  const {
    activeIndex: activeAtImageIndex,
    closeMenu: closeAtImageMenu,
    filteredOptions: filteredAtImageOptions,
    handleEnterCommand: handleAtImageEnterCommand,
    handleSuggestionKeyDown: handleAtImageKeyDown,
    insertOption: insertAtImage,
  } = useInlineSuggestionController({
    menuState: atImageMenuState,
    setMenuState: setAtImageMenuState,
    options: resolvedAtImageOptions,
    editorRef,
    isComposing,
    insertInlineBox: insertInlineBoxBySuggestion,
    createInlineBox: (option) => {
      const imgData = (option as AtImageOption).image;
      const mediaTypeMap: Record<string, InlineBoxType> = {
        video: InlineBoxType.Video,
        file: InlineBoxType.File,
        skill: InlineBoxType.Skill,
      };
      return {
        type: (imgData.mediaType && mediaTypeMap[imgData.mediaType]) || InlineBoxType.Image,
        data: imgData,
      };
    },
    onOptionSelect: (option) => onAtImageSelect?.(option as AtImageOption),
    allowEmpty: true,
  });

  const {
    activeIndex: activeSkillIndex,
    closeMenu: closeSkillMenu,
    filteredOptions: filteredSkillOptions,
    handleEnterCommand: handleSkillEnterCommand,
    handleSuggestionKeyDown: handleSkillKeyDown,
    insertOption: insertSkill,
  } = useInlineSuggestionController({
    menuState: skillMenuState,
    setMenuState: setSkillMenuState,
    options: skillOptions,
    editorRef,
    isComposing,
    insertInlineBox: insertInlineBoxBySuggestion,
    createInlineBox: (option) => ({
      type: InlineBoxType.Skill,
      data: (option as SkillOption).skill,
    }),
    onOptionSelect: (option) => onSkillSelect?.(option as SkillOption),
  });

  /** 只把键盘直接输入的 @ 和 / 作为联想弹框的首次触发来源。 */
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === '@' && !event.metaKey && !event.ctrlKey && !event.altKey) {
      allowNextAtImageTriggerRef.current = true;
    }
    if (isDirectSlashTriggerKey(event)) {
      allowNextSkillTriggerRef.current = true;
    }

    if (handleAtImageKeyDown(event)) {
      return;
    }
    if (handleSkillKeyDown(event)) {
      if (event.key === 'Escape') {
        skillMenuStateRef.current = null;
      }
      return;
    }
    handleMentionKeyDown(event);
  }, [
    handleAtImageKeyDown,
    handleMentionKeyDown,
    handleSkillKeyDown,
    setMentionMenuState,
  ]);

  const handleEnterCommand = useCallback(() => {
    if (handleAtImageEnterCommand()) return true;
    if (handleSkillEnterCommand()) return true;
    return handleMentionEnterCommand();
  }, [handleAtImageEnterCommand, handleMentionEnterCommand, handleSkillEnterCommand]);

  /** @图片联想仅响应首次键入 @；输入查询字符或光标切回已有 @ 时都不重新吊起。 */
  const handleAtImageMatchChange = useCallback((nextState: MentionMenuState | null) => {
    if (!nextState || nextState.query) {
      allowNextAtImageTriggerRef.current = false;
      atImageMenuStateRef.current = null;
      setAtImageMenuState(null);
      if (nextState) {
        setMentionMenuState(null);
        setSkillMenuState(null);
      }
      return;
    }

    if (atImageMenuStateRef.current) {
      atImageMenuStateRef.current = nextState;
      setAtImageMenuState(nextState);
      setMentionMenuState(null);
      setSkillMenuState(null);
      return;
    }

    if (!allowNextAtImageTriggerRef.current) {
      setAtImageMenuState(null);
      return;
    }

    allowNextAtImageTriggerRef.current = false;
    atImageMenuStateRef.current = nextState;
    setAtImageMenuState(nextState);
    setMentionMenuState(null);
    setSkillMenuState(null);
  }, [setMentionMenuState]);

  /** Slash 首次打开只接受物理键盘输入，正文前后直接键入 / 均立即展示候选。 */
  const handleSkillMatchChange = useCallback((nextState: MentionMenuState | null) => {
    if (!nextState) {
      allowNextSkillTriggerRef.current = false;
      skillMenuStateRef.current = null;
      setSkillMenuState(null);
      return;
    }

    if (skillMenuStateRef.current) {
      skillMenuStateRef.current = nextState;
      setSkillMenuState(nextState);
      setMentionMenuState(null);
      setAtImageMenuState(null);
      return;
    }

    if (!allowNextSkillTriggerRef.current) {
      setSkillMenuState(null);
      return;
    }

    allowNextSkillTriggerRef.current = false;
    skillMenuStateRef.current = nextState;
    setSkillMenuState(nextState);
    setMentionMenuState(null);
    setAtImageMenuState(null);
  }, [setMentionMenuState]);
  // ─── Mark 识别结果内部存储（数据驱动模式） ─────────────────────────────
  const markResultsMapRef = useRef(new Map<string, MarkResult[]>());
  const [markResultsVersion, setMarkResultsVersion] = useState(0);
  const notifyMarkResultsChange = useCallback(() => {
    queueMicrotask(() => {
      setMarkResultsVersion((v) => v + 1);
    });
  }, []);
  const getMarkResults = useCallback(
    (nodeKey: string) => markResultsMapRef.current.get(nodeKey),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markResultsVersion],
  );

  const handleContentChange = useCallback((params: { value: EditorSerializedContent; }) => {
    setEditorContent(params.value);
    onChange?.(params);
  }, [onChange]);

  const {
    handleOnChange,
    syncSignature,
  } = useEditorChangeHandler({
    onChange: handleContentChange,
    scrollCaretIntoView,
    markResultsMapRef,
  });

  const historyState = useMemo(() => createEmptyHistoryState(), []);

  const inputTextAreaContextValue = useMemo(() => ({
    renderMarkPopover,
    onInlineBoxClick,
    getMarkResults,
  }), [renderMarkPopover, onInlineBoxClick, getMarkResults]);

  const initialConfig = useMemo(() => ({
    namespace: 'RoboneoInputTextArea',
    nodes: [InlineBoxNode, MentionNode],
    editorState: initialValue?.length
      ? () => {
        const restoredResults = buildEditorFromContent(initialValue, { selectEnd: autoFocus });
        if (restoredResults.size) {
          restoredResults.forEach((results, key) => {
            markResultsMapRef.current.set(key, results);
          });
        }
      }
      : undefined,
    onError(error: Error) {
      console.error(error);
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const mentionPopupNode = useMemo(() => {
    if (!mentionMenuState || !filteredMentionOptions.length || !renderMentionPopup || useAtImageTrigger) return null;

    const popupProps = {
      activeIndex: activeMentionIndex,
      options: filteredMentionOptions,
      query: mentionMenuState.query,
      top: mentionMenuState.top,
      left: mentionMenuState.left,
      onSelect: insertMention,
      onKeyDown: handleKeyDown,
      onClose: closeMentionMenu,
    };

    return renderMentionPopup(popupProps);
  }, [
    activeMentionIndex,
    closeMentionMenu,
    filteredMentionOptions,
    insertMention,
    mentionMenuState,
    renderMentionPopup,
    handleKeyDown,
    useAtImageTrigger,
  ]);

  /** 默认 @ 图片联想弹框支持按业务场景切换到目标上方展示，避免靠近视窗底部时被截断。 */
  const defaultRenderAtImagePopup: AtImagePopupRenderer = useCallback((params) => (
    <SuggestionPopup
      top={params.top}
      left={params.left}
      placement={atImagePopupPlacement}
      options={params.options}
      activeIndex={params.activeIndex}
      emptyText={atImagePopupEmptyText}
      renderEmpty={params.renderEmpty}
      renderBeforeItem={params.renderBeforeItem}
      onKeyDown={params.onKeyDown}
      onSelect={params.onSelect}
    />
  ), [atImagePopupEmptyText, atImagePopupPlacement]);

  const defaultRenderSkillPopup: SkillPopupRenderer = useCallback((params) => (
    <SuggestionPopup
      top={params.top}
      left={params.left}
      options={params.options}
      activeIndex={params.activeIndex}
      renderEmpty={params.renderEmpty}
      renderBeforeItem={params.renderBeforeItem}
      onKeyDown={params.onKeyDown}
      onSelect={params.onSelect}
    />
  ), []);

  const resolvedRenderAtImagePopup = renderAtImagePopup || defaultRenderAtImagePopup;
  const resolvedRenderSkillPopup = renderSkillPopup || defaultRenderSkillPopup;

  const atImagePopupNode = useMemo(() => {
    if (!atImageMenuState) return null;

    return resolvedRenderAtImagePopup({
      activeIndex: activeAtImageIndex,
      options: filteredAtImageOptions as AtImageOption[],
      query: atImageMenuState.query,
      top: atImageMenuState.top,
      left: atImageMenuState.left,
      onSelect: (option) => insertAtImage(option),
      onKeyDown: handleKeyDown,
      onClose: closeAtImageMenu,
      renderEmpty: renderAtImagePopupEmpty,
      renderBeforeItem: renderAtImagePopupBeforeItem,
    });
  }, [
    activeAtImageIndex,
    atImageMenuState,
    closeAtImageMenu,
    filteredAtImageOptions,
    handleKeyDown,
    insertAtImage,
    renderAtImagePopupEmpty,
    renderAtImagePopupBeforeItem,
    resolvedRenderAtImagePopup,
  ]);

  const skillPopupNode = useMemo(() => {
    if (!skillMenuState || !filteredSkillOptions.length || !resolvedRenderSkillPopup) return null;

    return resolvedRenderSkillPopup({
      activeIndex: activeSkillIndex,
      options: filteredSkillOptions as SkillOption[],
      query: skillMenuState.query,
      top: skillMenuState.top,
      left: skillMenuState.left,
      onSelect: (option) => insertSkill(option),
      onKeyDown: handleKeyDown,
      onClose: closeSkillMenu,
      renderEmpty: renderSkillPopupEmpty,
      renderBeforeItem: renderSkillPopupBeforeItem,
    });
  }, [
    activeSkillIndex,
    closeSkillMenu,
    filteredSkillOptions,
    handleKeyDown,
    insertSkill,
    renderSkillPopupEmpty,
    renderSkillPopupBeforeItem,
    resolvedRenderSkillPopup,
    skillMenuState,
  ]);

  React.useEffect(() => {
    const shortcutOwner = historyShortcutOwnerRef.current;

    // 只接管当前编辑器内部的历史快捷键，避免页面其他区域撤销时被输入框抢焦点。
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (disabledRef.current || latestHistoryShortcutOwner !== shortcutOwner) {
        return;
      }

      const lowerCaseKey = event.key.toLowerCase();
      const isUndoShortcut = (event.metaKey || event.ctrlKey)
        && !event.shiftKey
        && lowerCaseKey === 'z';
      const isRedoShortcut = (event.metaKey && event.shiftKey && lowerCaseKey === 'z')
        || (event.ctrlKey && lowerCaseKey === 'y');

      if (!isUndoShortcut && !isRedoShortcut) {
        return;
      }

      const rootElement = editorRootRef.current;
      const editor = editorRef.current;
      if (!rootElement || !editor) {
        return;
      }

      const activeElement = document.activeElement as HTMLElement | null;
      const activeInsideEditor = activeElement
        ? rootElement === activeElement || rootElement.contains(activeElement)
        : false;
      const activeEditableOutsideEditor = shouldSkipInputHistoryShortcutForExternalEditable(
        activeElement,
        {
          activeInsideEditor,
          inputAreaRoot: inputContainerRef.current,
        },
      );

      // 其它可编辑区域（如标题输入框）自行处理 undo/redo。
      if (activeEditableOutsideEditor) {
        return;
      }

      // 画布等工作流区域自行处理 undo/redo，避免 Agent 输入框在 capture 阶段抢快捷键。
      if (shouldDeferInputHistoryShortcutToCanvas(activeElement)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      editor.dispatchCommand(isUndoShortcut ? UNDO_COMMAND : REDO_COMMAND, undefined);
    };

    document.addEventListener('keydown', handleDocumentKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown, true);
      if (latestHistoryShortcutOwner === shortcutOwner) {
        latestHistoryShortcutOwner = null;
      }
    };
  }, [disabledRef]);

  return (
    <div ref={inputContainerRef} className={`${classNames([styles.InputTextArea, className, 'RoboneoChatInputTextAreaContainer'])}`}>
      <LexicalComposer initialConfig={initialConfig}>
        <InputTextAreaContext.Provider value={inputTextAreaContextValue}>
          <EditorRefPlugin editorRef={editorRef} />
          <PlainTextPlugin
            contentEditable={(
              <ContentEditable
                ref={editorRootRef}
                className={classNames(
                  styles.textarea,
                  {
                    [styles.focused]: isFocused,
                    [styles.disabled]: disabled,
                  },
                  textareaClassName,
                  'RoboneoChatInputTextArea',
                )}
                onKeyDown={handleKeyDown}
                onFocus={(event) => {
                  markAsLatestHistoryShortcutOwner();
                  handleFocus();
                  onFocus?.(event);
                }}
                onBlur={(event) => handleBlur(() => {
                  closeMentionMenu();
                  closeAtImageMenu();
                  closeSkillMenu();
                  onBlur?.(event);
                })}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                aria-label={placeholder}
                data-inline-placeholder={inlinePlaceholder || undefined}
              />
            )}
            placeholder={<div className={classNames([styles.placeholder, placeholderClassName, 'RoboneoChatInputTextAreaPlaceholder'])}>{placeholder}</div>}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin externalHistoryState={historyState} />
          <DecoratorBackspacePlugin />
          <PostTaskSelectionGuardPlugin />
          <DecoratorHorizontalArrowPlugin isComposingRef={isComposingRef} />
          <DecoratorVerticalArrowPlugin isComposingRef={isComposingRef} />
          <OnChangePlugin
            onChange={handleOnChange}
            ignoreSelectionChange
          />
          {useAtImageTrigger ? (
            <MentionTriggerPlugin
              mentionOptions={resolvedAtImageOptions}
              alwaysTrigger
              allowEmptyQueryBeforeText
              editorRootRef={editorRootRef}
              onMatchChange={handleAtImageMatchChange}
            />
          ) : (
            <MentionTriggerPlugin
              mentionOptions={mentionOptions}
              editorRootRef={editorRootRef}
              onMatchChange={(nextState) => {
                setMentionMenuState(nextState);
                if (nextState) {
                  setSkillMenuState(null);
                }
              }}
            />
          )}
          {enableSkillSuggestion && (
            <SlashTriggerPlugin
              skillOptions={skillOptions}
              editorRootRef={editorRootRef}
              onMatchChange={handleSkillMatchChange}
            />
          )}
          <EditablePlugin disabled={disabled} />
          {autoFocus && <AutoFocusPlugin />}
          <MarkOrderTransformPlugin />
          <MaxLengthPlugin maxLength={maxLength} />
          <PastePlainTextPlugin maxLength={maxLength} />
          <EnterSubmitPlugin
            onEnter={onEnter}
            isComposingRef={isComposingRef}
            onBeforeEnter={handleEnterCommand}
            markResultsMapRef={markResultsMapRef}
          />
          <SelectionSyncPlugin onSelect={onSelect} />
          <InputBridgePlugin
            disabledRef={disabledRef}
            maxLength={maxLength}
            scrollCaretIntoView={scrollCaretIntoView}
            historyState={historyState}
            syncSignature={syncSignature}
            onProgrammaticEdit={markAsLatestHistoryShortcutOwner}
            registerInputBridge={handleRegisterBridge}
            afterInsertInlineNode={afterInsertInlineNode}
            mediaLimit={mediaLimit}
            onInsertRejected={onInsertRejected}
            fetchMarkResults={fetchMarkResults}
            markResultsMapRef={markResultsMapRef}
            notifyMarkResultsChange={notifyMarkResultsChange}
          />
        </InputTextAreaContext.Provider>
      </LexicalComposer>
      <PopupPortal containerRef={inputContainerRef}>
        {mentionPopupNode}
        {atImagePopupNode}
        {skillPopupNode}
      </PopupPortal>
    </div>
  );
});

export default memo(InputTextArea);
